import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {IVehiclePropertyBase, IVehiclePropertyColor, ICustomerVehicle, mergeAndAdd} from '../../store';
import {IBaseResponse, WebService} from '../../services';
import {findIndex} from 'lodash';

export interface IVehiclesStoreState {
	customerVehicles: ICustomerVehicle[];
	makes: IVehiclePropertyBase[];
	types: IVehiclePropertyBase[];
	colors: IVehiclePropertyColor[];
}

@Module({name: 'vehicles', namespaced: true})
class VehiclesModule extends VuexModule implements IVehiclesStoreState {
	customerVehicles: ICustomerVehicle[] = [];
	makes: IVehiclePropertyBase[] = [];
	types: IVehiclePropertyBase[] = [];
	colors: IVehiclePropertyColor[] = [];

	get customerVehicle() {
		return (id: string) => {
			return this.customerVehicles.find((vehicle) => {
				return vehicle.customerVehicleId === id;
			}) || null;
		};
	}

	get customerVehicleMatch() {
		return (color: IVehiclePropertyColor, make: IVehiclePropertyBase, type: IVehiclePropertyBase): ICustomerVehicle | null => {
			let match = null;
			for(const vehicle of this.customerVehicles) {
				// Just need to check against the prop value
				if(vehicle.color.value === color.value && vehicle.make.value === make.value && vehicle.type.value === type.value) {
					match = vehicle;
					break;
				}
			}
			return match;
		};
	}

	@Mutation
	setCustomerVehicles(vehicles: ICustomerVehicle[]) {
		this.customerVehicles = vehicles;
	}

	@Mutation
	setColors(colors: IVehiclePropertyColor[]) {
		this.colors = colors;
	}

	@Mutation
	setMakes(makes: IVehiclePropertyBase[]) {
		this.makes = makes;
	}

	@Mutation
	setTypes(types: IVehiclePropertyBase[]) {
		this.types = types;
	}

	@Mutation
	addCustomerVehicles(vehicles: ICustomerVehicle|ICustomerVehicle[]) {
		const newVehicles = Array.isArray(vehicles) ? vehicles : [vehicles];
		mergeAndAdd(this.customerVehicles, newVehicles, ['customerVehicleId']);
	}

	@Mutation
	removeCustomerVehicle(id: string) {
		const index = findIndex(this.customerVehicles, {
			customerVehicleId: id
		});
		if(index > -1) {
			this.customerVehicles.splice(index, 1);
		}
	}

	@Mutation
	clearCustomerVehicles() {
		this.customerVehicles = [];
	}

	@Action
	async fetchCustomerVehicles() {
		const result = await WebService.authenticatedClient.get<IBaseResponse<{vehicles: ICustomerVehicle[]}>>('vehicles');
		this.setCustomerVehicles(result.data.data.vehicles);
	}

	@Action
	async fetchVehicleProperties(locationId: string | null = null) {
		const result = await WebService.getOrderingClient(locationId).get<IBaseResponse<{makes: IVehiclePropertyBase[]; types: IVehiclePropertyBase[]; colors: IVehiclePropertyColor[]}>>('vehicle_properties');
		this.setColors(result.data.data.colors);
		this.setMakes(result.data.data.makes);
		this.setTypes(result.data.data.types);
	}

	@Action
	async saveCustomerVehicle(vehicle: ICustomerVehicle) {
		const result = await WebService.authenticatedClient.post<IBaseResponse<{vehicle: ICustomerVehicle}>>('vehicles/add', vehicle);
		this.addCustomerVehicles(result.data.data.vehicle);
		return result.data.data.vehicle.customerVehicleId;
	}

	@Action
	async deleteCustomerVehicle(id: string) {
		await WebService.authenticatedClient.post<IBaseResponse<{id: string}>>(`vehicles/delete/${id}`);
		this.removeCustomerVehicle(id);
		return id;
	}
}

export {
	VehiclesModule
};
